﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import * as React from "react";
import { isAndroid, isIOS } from "react-device-detect";
import { createUseStyles } from "react-jss";
import { Ingress } from "~base/Components/Primitives/Ingress";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import SplashPage from "~base/Pages/Shared/SplashPage";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";

import storeImageGooglePlay from "~jrad-resources/Images/google-play-badge.svg?url";
import storeImageAppStore from "~jrad-resources/Images/apple-appstore-badge.svg?url";
import { useSettings } from "~base/Settings/SettingsContext";

//-------------------------------------------------------------------------------------
// Styling
//-------------------------------------------------------------------------------------

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    page: {
        padding: "1rem",
        minWidth: "600px",

        "@media screen and (max-width: 600px)": {
            minWidth: "200px",
        },
    },
    store_container: {
        maxWidth: "600px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        flexWrap: "wrap",
    },
    store_image_link: {
        marginBottom: "2rem",
        marginLeft: "1rem",
        marginRight: "1rem",
    },
    store_image: {
        height: "5rem",
        width: "auto",

        "@media screen and (max-width: 600px)": {
            height: "auto",
            width: "90vw",
            maxWidth: "20rem",
        },
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const UppAppPageComponent: React.FunctionComponent<unknown> = () => {
    const classes = useStyles();
    const settings = useSettings();

    const hasDetectedDevice = isIOS || isAndroid;

    return (
        <SplashPage isLoading={false} showSplashScreen={false}>
            <div className={classes.page}>
                <Rubrik textCode="SoPNL.UI.AppVirtualPath.DownloadTheUppApp.PageTitle" />
                <Ingress
                    textCode={
                        hasDetectedDevice
                            ? "SoPNL.UI.AppVirtualPath.DownloadTheUppApp.PageContent.DeviceFound"
                            : "SoPNL.UI.AppVirtualPath.DownloadTheUppApp.PageContent.DeviceNotFound"
                    }
                />

                <div className={classes.store_container}>
                    {(isAndroid || !hasDetectedDevice) && (
                        <a
                            href={settings.valueAsString(
                                "DataGatheringAppPlayStoreUrl"
                            )}
                            className={classes.store_image_link}
                        >
                            <img
                                src={storeImageGooglePlay}
                                className={classes.store_image}
                            />
                        </a>
                    )}
                    {(isIOS || !hasDetectedDevice) && (
                        <a
                            href={settings.valueAsString(
                                "DataGatheringAppAppleAppStoreUrl"
                            )}
                            className={classes.store_image_link}
                        >
                            <img
                                src={storeImageAppStore}
                                className={classes.store_image}
                            />
                        </a>
                    )}
                </div>
            </div>
        </SplashPage>
    );
};

export default UppAppPageComponent;
